/**
 * L'elenco della pagina Fatture: in che ordine si mostrano i documenti e
 * quanto fanno, per tipo.
 *
 * L'ordine: dal giorno piu' recente, e nello stesso giorno dal numero piu'
 * alto. Prima si ordinava per data di inserimento in Pixel, che per i
 * documenti arrivati da Fatture in Cloud e' l'ordine della sincronizzazione:
 * 87, 88, 90, 89 nello stesso giorno.
 *
 * I totali seguono le regole del fatturato di Analisi aziendali
 * (kpiFatturato.ts): contano solo i documenti emessi, le note di credito si
 * tolgono, pro forma e DDT non sono fatturato. Prima la pagina sommava tutto
 * insieme, note di credito col segno sbagliato e pro forma comprese.
 */

import { TIPI_FISCALI, annoDi } from '../dashboard/kpiFatturato';

/** L'anno di competenza scelto in pagina: un anno, o tutti. */
export type AnnoScelto = number | 'tutti';

/** Le colonne che servono a ordinare e a fare i conti. */
export interface DocumentoElenco {
  invoice_number: string | null;
  invoice_date: string;
  created_at: string | null;
  document_type: string | null;
  status: string;
  total_amount: number | null;
  taxable_amount: number | null;
}

export interface NumeroDocumento {
  progressivo: number;
  /** L'anno scritto nel numero ("87/2026"); null se il numero non lo dice. */
  anno: number | null;
}

/** "87/2026" -> 87 e 2026. Le bozze non hanno numero. */
export function leggiNumero(numero: string | null | undefined): NumeroDocumento | null {
  if (!numero) return null;
  const conAnno = numero.match(/(\d+)\s*\/\s*(\d{4})/);
  if (conAnno) return { progressivo: Number(conAnno[1]), anno: Number(conAnno[2]) };
  const soloNumero = numero.match(/\d+/);
  return soloNumero ? { progressivo: Number(soloNumero[0]), anno: null } : null;
}

/**
 * Data piu' recente prima; nello stesso giorno i documenti ancora senza
 * numero in cima e poi il numero piu' alto. 0 se non c'e' differenza.
 */
export function confrontaDataNumero(
  a: { data: string | null; numero: string | null },
  b: { data: string | null; numero: string | null },
): number {
  const perData = (b.data ?? '').localeCompare(a.data ?? '');
  if (perData !== 0) return perData;

  const na = leggiNumero(a.numero);
  const nb = leggiNumero(b.numero);
  if (!na && nb) return -1;
  if (na && !nb) return 1;
  if (na && nb) {
    const perAnno = (nb.anno ?? 0) - (na.anno ?? 0);
    if (perAnno !== 0) return perAnno;
    return nb.progressivo - na.progressivo;
  }
  return 0;
}

/**
 * Per Array.sort: data piu' recente prima; nello stesso giorno le bozze
 * (ancora senza numero) in cima e poi il numero piu' alto.
 */
export function confrontaDocumenti(a: DocumentoElenco, b: DocumentoElenco): number {
  return (
    confrontaDataNumero({ data: a.invoice_date, numero: a.invoice_number }, { data: b.invoice_date, numero: b.invoice_number }) ||
    (b.created_at ?? '').localeCompare(a.created_at ?? '')
  );
}

export interface Somma {
  documenti: number;
  /** Senza IVA. */
  imponibile: number;
  /** Con IVA. */
  totale: number;
}

export interface RiepilogoDocumenti {
  /** Fatture e fatture accompagnatorie. */
  fatture: Somma;
  /** Importi positivi: nel netto si tolgono. */
  noteCredito: Somma;
  /** Fatture meno note di credito: il fatturato di Analisi aziendali. */
  netto: Somma;
  /** Non e' fatturato: si mostra a parte. */
  proforma: Somma;
  bozze: number;
}

export const sommaVuota = (): Somma => ({ documenti: 0, imponibile: 0, totale: 0 });

const piu = (s: Somma, doc: DocumentoElenco): Somma => ({
  documenti: s.documenti + 1,
  imponibile: s.imponibile + (doc.taxable_amount || 0),
  totale: s.totale + (doc.total_amount || 0),
});

/** I totali dei documenti dati, separati per tipo. */
export function riepilogoDocumenti(documenti: readonly DocumentoElenco[]): RiepilogoDocumenti {
  let fatture = sommaVuota();
  let noteCredito = sommaVuota();
  let proforma = sommaVuota();
  let bozze = 0;

  for (const doc of documenti) {
    if (doc.status === 'draft') bozze += 1;
    if (doc.status !== 'issued') continue;
    const tipo = doc.document_type || 'invoice';
    if (tipo === 'credit_note') noteCredito = piu(noteCredito, doc);
    else if (TIPI_FISCALI.has(tipo)) fatture = piu(fatture, doc);
    else if (tipo === 'proforma') proforma = piu(proforma, doc);
  }

  const netto: Somma = {
    documenti: fatture.documenti + noteCredito.documenti,
    imponibile: fatture.imponibile - noteCredito.imponibile,
    totale: fatture.totale - noteCredito.totale,
  };
  return { fatture, noteCredito, netto, proforma, bozze };
}

/**
 * Gli anni da proporre nella scelta dell'anno di competenza, dal piu'
 * recente: dal primo documento all'ultimo, con sempre l'anno in corso.
 */
export function anniDiCompetenza(
  primaData: string | null | undefined,
  ultimaData: string | null | undefined,
  annoCorrente: number,
): number[] {
  const primo = Math.min(annoDi(primaData) ?? annoCorrente, annoCorrente);
  const ultimo = Math.max(annoDi(ultimaData) ?? annoCorrente, annoCorrente);
  const anni: number[] = [];
  for (let anno = ultimo; anno >= primo; anno -= 1) anni.push(anno);
  return anni;
}
